import { createSelector } from 'reselect';
import { IAppState } from 'spw-redux/reducer';
import {
  HEADCOUNTS,
  IBuilding,
  IFloor,
} from 'spw-redux/modules/projects/types';
import { calculateLevelHeadcount } from 'spw-esa-projects/program-summary/utils';
import { currentProject } from '../../projects/selectors';

export const selectUsfTotal = (state: IAppState) =>
  state.programSummaryReducer.usfTotal;

export const selectRsfTotal = (state: IAppState) =>
  state.programSummaryReducer.rsfTotal;

export const currentSelectedType = (state: IAppState) =>
  state.programSummaryReducer.selectedType;

export const selectKeyData = (state: IAppState) =>
  state.programSummaryReducer.keyData;

export const selectBreakdown = (state: IAppState) =>
  state.programSummaryReducer.breakdown;

export const selectSelectedLevels = (state: IAppState) =>
  state.programSummaryReducer.selectedLevels;

export const selectAltWorkpointsBreakdown = (state: IAppState) =>
  state.programSummaryReducer.alternativeWorkpointsBreakdown;

export const selectProgramSummaryCardIcons = (state: IAppState) =>
  state.projectsReducer.currentProject.programSummaryCardIcons || [];

export const selectTotalWorkpoints = createSelector(
  selectKeyData,
  selectAltWorkpointsBreakdown,
  (keyData, altWorkpointsBreakdown) => {
    return keyData.workseats + altWorkpointsBreakdown.all.seatsCount;
  }
);

export const selectSelectedLevelsExpanded = createSelector(
  currentProject,
  selectSelectedLevels,
  (
    project,
    selectedLevels
  ): {
    allSelectedBuildingIds: IBuilding['id'][];
    allSelectedFloorIds: IFloor['id'][];
  } => {
    const { floorIds, buildingIds } = selectedLevels;
    const fullySelectedBuildings = project?.buildings.filter((b) =>
      buildingIds.includes(b.id)
    );
    const floorIdsFromFullySelectedBuildings = fullySelectedBuildings.reduce(
      (fIds, b) => [...fIds, ...b.floors.map((bF) => bF.id)],
      []
    );
    // find the buildings of which only a subset of their floors have been selected
    // these buildings are not included in selectSelectedLevels buildingIds
    const partiallySelectedBuildingIds = project?.buildings.reduce(
      (foundBuildingIds, b: IBuilding) => {
        if (b.floors.some((f) => floorIds.includes(f.id))) {
          foundBuildingIds.push(b.id);
        }
        return foundBuildingIds;
      },
      []
    );

    return {
      allSelectedBuildingIds: [...buildingIds, ...partiallySelectedBuildingIds],
      allSelectedFloorIds: [...floorIds, ...floorIdsFromFullySelectedBuildings],
    };
  }
);

export const selectProgramSummaryBuildingsSelected = createSelector(
  selectSelectedLevels,
  currentProject,
  (selectedLevels, project) => {
    return project.buildings.filter(
      (b) =>
        selectedLevels.buildingIds.includes(b.id) ||
        b.floors.some((f) => selectedLevels.floorIds.includes(f.id))
    );
  }
);

export const selectLevelHeadcount = createSelector(
  selectSelectedLevels,
  currentProject,
  (selectedLevels, project) => {
    return calculateLevelHeadcount(project, selectedLevels);
  }
);
export const selectHeadcountValue = createSelector(
  selectLevelHeadcount,
  currentProject,
  (levelHeadcount, project) => {
    return project.headcountType === HEADCOUNTS.ARCHITECT
      ? levelHeadcount.architectHeadcount
      : levelHeadcount.clientHeadcount;
  }
);

export const selectHeadCountRsf = createSelector(
  selectRsfTotal,
  selectHeadcountValue,
  ({ totalRsf }, headcountValue) => {
    if (!totalRsf || !headcountValue) return 0;
    return Math.round(totalRsf / headcountValue);
  }
);

export const selectHeadcountUsf = createSelector(
  selectKeyData,
  selectHeadcountValue,
  ({ total }, headcountValue) => {
    if (!total || !headcountValue) return 0;

    return Math.round(total / headcountValue);
  }
);

export const selectSeatCountRsf = createSelector(
  selectRsfTotal,
  selectKeyData,
  ({ totalRsf }, keyData) => {
    if (!totalRsf || !keyData.workseats) return 0;
    return Math.round(totalRsf / keyData.workseats);
  }
);

export const selectSeatCountRsfWithWorkpoints = createSelector(
  selectRsfTotal,
  selectTotalWorkpoints,

  ({ totalRsf }, totalWorkpoints) => {
    if (!totalRsf || !totalWorkpoints) return 0;
    return Math.round(totalRsf / totalWorkpoints);
  }
);
